import { useState } from 'react'


import { WhatsappIcon } from '@hugeicons/react'

import { api } from '@/shared/lib/api'
import { showErrorAlert } from '@/shared/lib/errors'
import { Button } from '@/shared/ui'
import { RadioRowField } from '@/shared/ui/Form'

import { whatsappHref } from './whatsapp'

// Plataforma controla o texto de consentimento (decisão D3): base legal
// consistente, não editável pelo parceiro.
const CONSENT_TEXT =
  'Autorizo o compartilhamento dos meus dados de contato com este parceiro para que ele possa entrar em contato comigo sobre seus produtos e serviços.'

// Field border in resting state uses the DS field-border token (border-input,
// gray-600) rather than the handoff's pale #E5E0F5, which fails the project's
// normative field-contrast floor (ACCESSIBILITY.md, WCAG 1.4.11 ≥3:1). Selected
// state carries the brand accent.
const inputClass =
  'h-12 w-full rounded-xl border border-input bg-white px-3 text-base text-gray-900 outline-none transition-colors placeholder:text-gray-700 focus:border-brand focus:ring-[3px] focus:ring-brand/15'

const QuestionLabel = ({ children, required }) => (
  <span className="text-sm font-semibold text-gray-800">
    {children}
    {required && <span className="text-danger-text"> *</span>}
  </span>
)

// Renders one dynamic question of the jsonb capture form (D6). Answers are keyed
// by the field id: `single` → the selected alternative title (string), `multi` →
// an array of selected titles, `number`/`text` → the raw string value.
const QuestionField = ({ item, value, onChange }) => {
  if (item.type === 'single' || item.type === 'multi') {
    const isMulti = item.type === 'multi'
    const selected = isMulti
      ? (Array.isArray(value) ? value : [])
      : (value ? [value] : [])
    const alternatives = item.alternatives || []

    const toggle = title => {
      if (isMulti) {
        const next = selected.includes(title)
          ? selected.filter(t => t !== title)
          : [...selected, title]
        onChange(next)
      } else {
        onChange(selected.includes(title) ? '' : title)
      }
    }

    // multi → pill chips; single → radio rows.
    if (isMulti) {
      return (
        <fieldset className="border-0 p-0">
          <legend className="mb-2 p-0">
            <QuestionLabel required={item.required}>{item.title}</QuestionLabel>
          </legend>
          <div className="flex flex-wrap gap-2">
            {alternatives.map(alt => {
              const on = selected.includes(alt.title)
              return (
                <label
                  key={alt.id}
                  className={`relative inline-flex min-h-11 cursor-pointer items-center rounded-full border px-4 text-sm font-semibold transition-colors ${
                    on
                      ? 'border-brand bg-brand text-[var(--color-brand-inverse)]'
                      : 'border-input bg-white text-gray-800'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={on}
                    onChange={() => toggle(alt.title)}
                  />
                  {alt.title}
                </label>
              )
            })}
          </div>
        </fieldset>
      )
    }

    return (
      <RadioRowField
        legend={<QuestionLabel required={item.required}>{item.title}</QuestionLabel>}
        value={selected[0] ?? ''}
        onChange={next => onChange(next)}
        allowDeselect
        groupName={`q-${item.id}`}
        options={alternatives.map(alt => ({ value: alt.title, label: alt.title }))}
      />
    )
  }

  if (item.type === 'number') {
    return (
      <label className="flex flex-col gap-1.5">
        <QuestionLabel required={item.required}>{item.title}</QuestionLabel>
        <input
          type="number"
          inputMode="numeric"
          min={item.min ?? undefined}
          max={item.max ?? undefined}
          value={value}
          onChange={e => onChange(e.target.value)}
          className={inputClass}
        />
      </label>
    )
  }

  // text (and any other type) → free text.
  return (
    <label className="flex flex-col gap-1.5">
      <QuestionLabel required={item.required}>{item.title}</QuestionLabel>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className={inputClass}
      />
    </label>
  )
}

/**
 * Consent-gated lead capture form for the premium partner profile (handoff
 * bloco 7). Renders the partner's configured questions dynamically; submission
 * is only enabled with all required answers plus the consent checkbox
 * (requirements 3.3/3.4). Posts to the public lead endpoint, which upserts (D1).
 * On success the card body is replaced by a confirmation that offers the
 * WhatsApp shortcut — the moment of peak intent.
 */
export const PartnerLeadForm = ({
  partner,
  digitalProductId,
  form,
  source = 'profile_form',
  alreadySubmitted = false,
  onSuccess
}) => {
  const items = form?.items || []
  const [answers, setAnswers] = useState({})
  const [message, setMessage] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Seeded from the partner profile's `lead_submitted` flag (api) so a lead
  // captured in a previous visit renders the confirmation immediately instead
  // of a blank, resubmittable form (bug: partner-form-submission-status-not-persisted).
  const [done, setDone] = useState(alreadySubmitted)

  const whatsapp = whatsappHref(partner?.meta?.whatsapp)
  const setAnswer = (id, value) => setAnswers(prev => ({ ...prev, [id]: value }))

  // An answer is present when it has a non-empty array (multi) or a non-blank
  // string (text/number/single).
  const isAnswered = value =>
    Array.isArray(value) ? value.length > 0 : String(value ?? '').trim() !== ''

  const missingRequired = items.some(
    item => item.required && !isAnswered(answers[item.id])
  )

  const submit = async event => {
    event.preventDefault()
    if (!consent || submitting || missingRequired) return
    setSubmitting(true)

    const payloadAnswers = items.length ? answers : message ? { message } : {}

    try {
      await api.post('/public/partner_leads', {
        record: {
          partner_uid: partner.uid,
          digital_product_id: digitalProductId,
          source,
          consent: true,
          answers: payloadAnswers
        }
      })
      setDone(true)
      onSuccess?.()
    } catch (error) {
      showErrorAlert(error)
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <section className="rounded-[18px] bg-white p-[18px] text-center shadow-[0_2px_10px_rgba(76,29,149,.07)]">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#DCFCE7] text-2xl font-bold text-[#16A34A]">
          ✓
        </div>
        <h2 className="mx-auto mt-3 text-lg font-extrabold text-gray-900">Interesse enviado!</h2>
        <p className="mx-auto mt-1.5 mb-0 text-sm leading-relaxed text-gray-700">
          O parceiro recebeu seus dados e vai falar com você.
          {whatsapp ? ' Quer adiantar a conversa?' : ''}
        </p>
        {whatsapp && (
          <Button
            href={whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            variant="whatsapp"
            size="lg"
            className="mt-3.5 w-full gap-2"
            leadingIcon={<WhatsappIcon size={18} variant="stroke" />}
            showTrailingIcon={false}
          >
            Falar no WhatsApp agora
          </Button>
        )}
      </section>
    )
  }

  return (
    <section className="rounded-[18px] bg-white p-[18px] shadow-[0_2px_10px_rgba(76,29,149,.07)]">
      <h2 className="m-0 text-[17px] font-bold leading-tight text-gray-900">
        {form?.title || 'Quer que eles falem com você?'}
      </h2>
      {items.length > 0 && (
        <p className="mt-1 mb-0 text-[13.5px] leading-relaxed text-gray-700">
          {items.length} {items.length === 1 ? 'pergunta rápida' : 'perguntas rápidas'} — leva menos
          de 1 minuto.
        </p>
      )}

      <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
        {items.length > 0 ? (
          items.map(item => (
            <QuestionField
              key={item.id}
              item={item}
              value={answers[item.id] || ''}
              onChange={value => setAnswer(item.id, value)}
            />
          ))
        ) : (
          <label className="flex flex-col gap-1.5">
            <QuestionLabel>Mensagem (opcional)</QuestionLabel>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              placeholder="Conte ao parceiro o que você procura"
              className={`${inputClass} h-auto py-2.5`}
            />
          </label>
        )}

        <label className="relative flex cursor-pointer items-start gap-2.5 rounded-2xl border border-brand/15 bg-brand-light/40 p-3">
          <input
            type="checkbox"
            className="sr-only"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
          />
          <span
            className={`mt-0.5 grid h-[22px] w-[22px] shrink-0 place-items-center rounded-[7px] text-[13px] font-bold text-white ${
              consent ? 'bg-brand' : 'border border-input bg-white'
            }`}
            aria-hidden
          >
            {consent ? '✓' : ''}
          </span>
          <span className="text-[13px] leading-relaxed text-gray-700">
            {CONSENT_TEXT}
            <span className="text-danger-text"> *</span>
          </span>
        </label>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!consent || submitting || missingRequired}
          loading={submitting}
        >
          Enviar interesse
        </Button>
      </form>
    </section>
  )
}
